import { ChartNode } from '../models/chart-node.model';

export interface Size {
  width: number;
  height: number;
}

// One page/slide of a paged export: `root` is a depth-limited copy of `source` to render.
export interface ChartSection {
  root: ChartNode;
  source: ChartNode;
  // Ancestors of `source`, top first.
  path: ChartNode[];
  part: number;
  parts: number;
  // Nodes on this page whose children continue in another section.
  continued: ChartNode[];
}

export interface PaginationOptions {
  // The area a section is fitted into, in the same units as `measure` (CSS px at zoom 1).
  box: Size;
  // Smallest scale a section may be shrunk to and still be readable.
  minScale: number;
  // Renders a tree and returns its natural size.
  measure: (root: ChartNode) => Size;
  // Children that should be included (lets filters hide nodes).
  childrenOf?: (node: ChartNode) => ChartNode[];
}

// Copy of `node` showing `depth` levels below it. Below that, nodes are collapsed but keep their
// real children so cards still show how many there are.
export function cloneToDepth(node: ChartNode, depth: number, childrenOf: (node: ChartNode) => ChartNode[], children?: ChartNode[]): ChartNode {
  const shown = children ?? childrenOf(node);
  const expanded = depth > 0 && shown.length > 0;
  return {
    ...node,
    expanded,
    children: expanded ? shown.map(child => cloneToDepth(child, depth - 1, childrenOf)) : node.children,
    childCount: node.childCount ?? node.children.length,
  };
}

export function subtreeHeight(node: ChartNode, childrenOf: (node: ChartNode) => ChartNode[]): number {
  const children = childrenOf(node);
  return children.length ? 1 + Math.max(...children.map(child => subtreeHeight(child, childrenOf))) : 0;
}

// Splits a chart into sections that each fit `box` at `minScale` or larger.
// Each section shows a node and as many levels below it as fit. Nodes on the bottom level that
// have children start sections of their own. When even a node's direct children don't fit, they
// are split across several sections (parts).
export function planSections(roots: ChartNode[], options: PaginationOptions): ChartSection[] {
  const childrenOf = options.childrenOf ?? (node => node.children);
  const fits = (size: Size) =>
    size.width * options.minScale <= options.box.width && size.height * options.minScale <= options.box.height;

  const sections: ChartSection[] = [];
  const queue: { node: ChartNode, path: ChartNode[] }[] = roots.map(node => ({ node, path: [] }));

  while (queue.length) {
    const { node, path } = queue.shift();
    const children = childrenOf(node);
    const childPath = [...path, node];

    // Deepest number of levels that still fits
    const height = subtreeHeight(node, childrenOf);
    let depth = 0;
    while (depth < height && fits(options.measure(cloneToDepth(node, depth + 1, childrenOf)))) {
      depth++;
    }

    if (depth > 0 || !children.length) {
      const continued = boundaryNodes(node, depth, childrenOf);
      sections.push({ root: cloneToDepth(node, depth, childrenOf), source: node, path, part: 1, parts: 1, continued });
      continued.forEach(child => queue.push({ node: child, path: [...childPath, ...pathBetween(node, child, childrenOf)] }));
      continue;
    }

    // Too many direct children for one page: split them into chunks that each fit
    const chunks: ChartNode[][] = [];
    let start = 0;
    while (start < children.length) {
      let low = 1;
      let high = children.length - start;
      while (low < high) {
        const mid = Math.ceil((low + high) / 2);
        if (fits(options.measure(cloneToDepth(node, 1, childrenOf, children.slice(start, start + mid))))) {
          low = mid;
        } else {
          high = mid - 1;
        }
      }
      chunks.push(children.slice(start, start + low));
      start += low;
    }

    chunks.forEach((chunk, index) => {
      const continued = chunk.filter(child => childrenOf(child).length);
      sections.push({
        root: cloneToDepth(node, 1, childrenOf, chunk),
        source: node,
        path,
        part: index + 1,
        parts: chunks.length,
        continued,
      });
      continued.forEach(child => queue.push({ node: child, path: childPath }));
    });
  }

  return sections;
}

// Nodes exactly `depth` levels below `node` that have children of their own.
function boundaryNodes(node: ChartNode, depth: number, childrenOf: (node: ChartNode) => ChartNode[]): ChartNode[] {
  let level = [node];
  for (let i = 0; i < depth; i++) {
    level = level.flatMap(childrenOf);
  }
  return depth === 0 ? [] : level.filter(child => childrenOf(child).length);
}

// Nodes strictly between `ancestor` and `descendant`.
function pathBetween(ancestor: ChartNode, descendant: ChartNode, childrenOf: (node: ChartNode) => ChartNode[]): ChartNode[] {
  for (const child of childrenOf(ancestor)) {
    if (child === descendant) {
      return [];
    }
    const below = pathBetween(child, descendant, childrenOf);
    if (below) {
      return [child, ...below];
    }
  }
  return null;
}
